import React, { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, ArrowRight } from 'lucide-react';
import { EntityType, EntityUsage, ReassignStats } from '../../types';

/**
 * B1 — borrado de un instructor, un nivel o una sala.
 *
 * Los tres comparten flujo, así que comparten modal (mismo criterio que
 * NivelInstructorModal). Tres pasos, encadenados según lo que diga `usage.action`:
 *
 *   'delete'   → confirmación simple; no se usa en ningún sitio.
 *   'archive'  → confirmación explicando que se archiva y por qué.
 *   'reassign' → bloqueo + selector de destino → tras reasignar, se vuelve a
 *                preguntar si se archiva (paso 'archive').
 *
 * No se reutiliza ConfirmationModal porque su único punto de extensión es
 * `showOptions` y no admite un desplegable.
 */

export interface EntityOption {
    _id: string;
    name: string;
}

interface Props {
    isOpen: boolean;
    entityType: EntityType;
    name: string;
    /** Las demás entidades del mismo tipo, ya sin la que se va a eliminar. */
    options: EntityOption[];
    usage?: EntityUsage;
    isLoadingUsage: boolean;
    isReassigning: boolean;
    isDeleting: boolean;
    /** Resultado de la reasignación recién hecha; abre el paso de archivado. */
    completedReassign?: { target: string; stats: ReassignStats };
    onReassign: (targetId: string) => void;
    onDelete: () => void;
    onClose: () => void;
}

// Texto visible: en español. `noun` y `demonstrative` concuerdan en género.
const LABELS: Record<EntityType, { noun: string; demonstrative: string; placeholder: string }> = {
    instructor: { noun: 'instructor', demonstrative: 'este', placeholder: 'Selecciona un instructor' },
    level: { noun: 'nivel', demonstrative: 'este', placeholder: 'Selecciona un nivel' },
    classroom: { noun: 'sala', demonstrative: 'esta', placeholder: 'Selecciona una sala' },
};

const pluralize = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`;

/** Frases del tipo "3 clases futuras, 1 plantilla semanal y 2 plazas activas". */
const describeUsage = (usage: EntityUsage): string => {
    const parts: string[] = [];
    if (usage.futureEvents) parts.push(pluralize(usage.futureEvents, 'clase futura', 'clases futuras'));
    if (usage.templates) parts.push(pluralize(usage.templates, 'plantilla semanal', 'plantillas semanales'));
    if (usage.slots) parts.push(pluralize(usage.slots, 'plaza activa', 'plazas activas'));
    if (usage.recoveries) parts.push(pluralize(usage.recoveries, 'recuperación pendiente', 'recuperaciones pendientes'));
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
};

const describeReassign = (stats: ReassignStats): string => {
    const parts = [pluralize(stats.eventsUpdated, 'clase', 'clases')];
    if (stats.templatesUpdated) parts.push(pluralize(stats.templatesUpdated, 'plantilla', 'plantillas'));
    if (stats.slotsUpdated) parts.push(pluralize(stats.slotsUpdated, 'plaza', 'plazas'));
    if (stats.recoveriesUpdated) parts.push(pluralize(stats.recoveriesUpdated, 'recuperación', 'recuperaciones'));
    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
};

const DeleteEntityModal: React.FC<Props> = ({
    isOpen, entityType, name, options, usage, isLoadingUsage,
    isReassigning, isDeleting, completedReassign,
    onReassign, onDelete, onClose,
}) => {
    const [targetId, setTargetId] = useState('');
    const labels = LABELS[entityType];
    const isFeminine = entityType === 'classroom';
    const busy = isReassigning || isDeleting;

    // Tras una reasignación correcta ya no queda uso futuro, así que el paso que
    // toca es el de archivado aunque `usage` siga trayendo los contadores viejos.
    const step = useMemo(() => {
        if (completedReassign) return 'archive';
        return usage?.action ?? 'delete';
    }, [completedReassign, usage]);

    // Clases que se quedan apuntando a esta entidad tras archivarla. `usage` es la
    // foto de ANTES de reasignar, así que hay que descontar las futuras que se
    // acaban de mover; sin esto, el aviso diría "312 clases ya impartidas" cuando
    // en realidad son 312 menos las 48 reasignadas. En el archivado directo
    // (sin reasignación) futureEvents es 0 y la resta no cambia nada.
    const pastEvents = usage ? usage.events - usage.futureEvents : 0;

    if (!isOpen) return null;

    const close = () => {
        if (busy) return;
        setTargetId('');
        onClose();
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="bg-white p-6 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center mb-4">
                    <AlertTriangle size={32} className="text-yellow-500 mr-3 shrink-0" />
                    <h2 className="font-bold text-lg">
                        {step === 'reassign' ? `No se puede eliminar «${name}»` : `Eliminar «${name}»`}
                    </h2>
                </div>

                {isLoadingUsage && (
                    <div className="flex items-center text-gray-600 py-6">
                        <Loader2 size={18} className="animate-spin mr-2" />
                        Comprobando si se está usando…
                    </div>
                )}

                {!isLoadingUsage && usage && (
                    <>
                        {/* Paso 1a — nada lo usa: se borra de verdad. */}
                        {step === 'delete' && (
                            <p className="mb-5">
                                «{name}» no se usa en ninguna clase. Se eliminará definitivamente.
                                ¿Quieres continuar?
                            </p>
                        )}

                        {/* Paso 1b / 3 — solo uso pasado: se archiva. */}
                        {step === 'archive' && (
                            <div className="mb-5 space-y-3">
                                {completedReassign && (
                                    <div className="bg-green-50 border-l-4 border-green-500 p-3 text-sm">
                                        Reasignación completada:{' '}
                                        <strong>{describeReassign(completedReassign.stats)}</strong>{' '}
                                        {completedReassign.stats.eventsUpdated === 1 ? 'pasa' : 'pasan'} a
                                        usar «{completedReassign.target}».
                                    </div>
                                )}
                                <p>
                                    «{name}» ya no se usa en ninguna clase futura.
                                    ¿Quieres archivarlo ahora?
                                </p>
                                <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-sm">
                                    Se archivará en lugar de borrarse: dejará de aparecer en los
                                    desplegables, pero{' '}
                                    <strong>{pluralize(pastEvents, 'clase ya impartida', 'clases ya impartidas')}</strong>{' '}
                                    {pastEvents === 1 ? 'seguirá mostrándolo' : 'seguirán mostrándolo'}.
                                </div>
                            </div>
                        )}

                        {/* Paso 2 — hay uso futuro: hay que reasignar antes. */}
                        {step === 'reassign' && (
                            <div className="mb-5 space-y-4">
                                <p>
                                    {isFeminine ? 'Esta sala está asignada' : `Este ${labels.noun} está asignado`} a{' '}
                                    <strong>{describeUsage(usage)}</strong>. Para eliminarlo hay que reasignar
                                    ese uso a otr{isFeminine ? 'a' : 'o'} {labels.noun}.
                                </p>

                                {options.length === 0 ? (
                                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 text-sm">
                                        No hay otr{isFeminine ? 'a' : 'o'} {labels.noun} al que
                                        reasignar. Crea {isFeminine ? 'una' : 'uno'} antes de eliminar{' '}
                                        {labels.demonstrative}.
                                    </div>
                                ) : (
                                    <>
                                        <label className="block">
                                            <span className="text-sm font-medium text-gray-700">Reasignar a:</span>
                                            <select
                                                value={targetId}
                                                onChange={(e) => setTargetId(e.target.value)}
                                                disabled={busy}
                                                className="mt-1 p-2 border rounded w-full"
                                            >
                                                <option value="">{labels.placeholder}</option>
                                                {options.map((o) => (
                                                    <option key={o._id} value={o._id}>{o.name}</option>
                                                ))}
                                            </select>
                                        </label>

                                        <div className="bg-gray-50 border-l-4 border-gray-400 p-3 text-sm">
                                            Solo se mueven las clases de hoy en adelante. Las ya impartidas
                                            se quedan como están, para no reescribir el historial.
                                        </div>

                                        {entityType === 'classroom' && (
                                            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 text-sm">
                                                El aforo de las clases no cambia al reasignar. Si la sala de
                                                destino tiene una capacidad distinta, revísalo después.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}

                <div className="flex justify-end gap-3">
                    <button
                        onClick={close}
                        disabled={busy}
                        className="py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {step === 'archive' && completedReassign ? 'Ahora no' : 'Cancelar'}
                    </button>

                    {!isLoadingUsage && step === 'reassign' && options.length > 0 && (
                        <button
                            onClick={() => onReassign(targetId)}
                            disabled={busy || !targetId}
                            className="py-2 px-4 rounded-md text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                        >
                            {isReassigning ? (
                                <><Loader2 size={16} className="animate-spin mr-2" />Reasignando…</>
                            ) : (
                                <><ArrowRight size={16} className="mr-2" />Reasignar</>
                            )}
                        </button>
                    )}

                    {!isLoadingUsage && (step === 'delete' || step === 'archive') && (
                        <button
                            onClick={onDelete}
                            disabled={busy}
                            className="py-2 px-4 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 flex items-center"
                        >
                            {isDeleting ? (
                                <><Loader2 size={16} className="animate-spin mr-2" />Procesando…</>
                            ) : (
                                step === 'archive' ? 'Archivar' : 'Eliminar'
                            )}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteEntityModal;
